import {
  ApiErrorSchema,
  AuthResponse,
  type Child,
  ChildSchema,
  type Entitlement,
  MeResponse,
  type Parent,
  ParentSchema,
  PinResponse,
  ShareCreateResponse,
  type WindowsConfig,
} from '@disdis/shared'
import type { z } from 'zod'
import { log, reason } from '../app/log.js'
import { apiBaseUrl } from '../state/sync.js'
import { getParentSession } from './session.js'

/**
 * The parent area's client for API.md §1, §2, §3, §6, §7 and §9. Everything the child plays
 * still goes through `state/sync.ts`; this file only carries parent account traffic.
 */

const logger = log('parent-api')

export class ParentApiError extends Error {
  readonly code: string
  readonly status: number
  constructor(code: string, message: string, status: number) {
    super(message)
    this.name = 'ParentApiError'
    this.code = code
    this.status = status
  }
}

/** True when the request never reached the server, so the UI can say "offline" honestly. */
export function isOffline(error: unknown): boolean {
  return error instanceof ParentApiError && error.status === 0
}

async function call<T>(
  path: string,
  schema: z.ZodType<T> | null,
  init?: RequestInit,
): Promise<T | null> {
  const session = await getParentSession()
  let response: Response
  try {
    response = await fetch(`${apiBaseUrl()}${path}`, {
      credentials: 'include',
      ...init,
      headers: {
        'content-type': 'application/json',
        ...(session ? { authorization: `Bearer ${session.token}` } : {}),
        ...(init?.headers ?? {}),
      },
    })
  } catch (error) {
    logger.warn(`${path} did not reach the server`, reason(error))
    throw new ParentApiError('offline', 'offline', 0)
  }

  if (!response.ok) {
    const body = await response.json().catch(() => null)
    const parsed = ApiErrorSchema.safeParse(body)
    throw new ParentApiError(
      parsed.success ? parsed.data.error.code : 'http_error',
      parsed.success ? parsed.data.error.message : `${response.status}`,
      response.status,
    )
  }
  if (schema === null) return null
  return schema.parse(await response.json())
}

async function post<T>(path: string, schema: z.ZodType<T> | null, body?: unknown): Promise<T> {
  return (await call(path, schema, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })) as T
}

/* --------------------------------------------------------------------- auth */

export type AuthResult = { parent: Parent; token: string; expiresAt: string }

export async function register(
  email: string,
  password: string,
  locale: 'tr' | 'en' = 'tr',
): Promise<AuthResult> {
  const body = await post('/auth/register', AuthResponse, { email, password, locale })
  return { parent: body.parent, token: body.session.token, expiresAt: body.session.expiresAt }
}

export async function login(email: string, password: string): Promise<AuthResult> {
  const body = await post('/auth/login', AuthResponse, { email, password })
  return { parent: body.parent, token: body.session.token, expiresAt: body.session.expiresAt }
}

export async function logout(): Promise<void> {
  await call('/auth/logout', null, { method: 'POST' })
}

export type MeResult = {
  parent: Parent
  children: Child[]
  entitlement: Entitlement
  flags: z.infer<typeof MeResponse>['flags']
}

export async function me(): Promise<MeResult> {
  return (await call('/auth/me', MeResponse)) as MeResult
}

/* -------------------------------------------------------- consents and pin */

export async function recordConsent(
  type: 'kvkk' | 'camera' | 'research',
  version: string,
  granted: boolean,
): Promise<void> {
  await post('/consents', null, { type, version, granted })
}

/** Returns the argon2id hash so the gate can verify the PIN offline (API.md §2). */
export async function setPin(pin: string): Promise<string | null> {
  const body = (await call('/pin', PinResponse, {
    method: 'PUT',
    body: JSON.stringify({ pin }),
  })) as z.infer<typeof PinResponse>
  return body.pinHash ?? null
}

export async function verifyPinOnline(pin: string): Promise<boolean> {
  try {
    await post('/pin/verify', null, { pin })
    return true
  } catch (error) {
    if (error instanceof ParentApiError && error.code === 'bad_pin') return false
    throw error
  }
}

/* ----------------------------------------------------------------- children */

export type ChildDraft = {
  nickname: string
  ageBand: '5-7' | '8-12'
  avatarId: string
  petName: string
}

export async function createChild(draft: ChildDraft): Promise<Child> {
  return await post('/children', ChildSchema, draft)
}

export async function patchChild(id: string, patch: Partial<ChildDraft & { mode: string }>) {
  return (await call(`/children/${id}`, ChildSchema, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })) as Child
}

export async function deleteChild(id: string): Promise<void> {
  await call(`/children/${id}`, null, { method: 'DELETE' })
}

export async function applyReviewCode(id: string, code: string): Promise<void> {
  await post(`/children/${id}/review-mode`, null, { code })
}

/* -------------------------------------------------------------------- share */

export type ShareCode = { code: string; expiresAt: string }

export async function createShareCode(childId: string): Promise<ShareCode> {
  return await post('/share', ShareCreateResponse, { childId })
}

export async function revokeShareCode(code: string): Promise<void> {
  await call(`/share/${code}`, null, { method: 'DELETE' })
}

/* --------------------------------------------------------------------- push */

export async function subscribePush(
  childId: string,
  subscription: PushSubscriptionJSON,
): Promise<void> {
  await post('/push/subscribe', null, { childId, subscription })
}

/* ------------------------------------------------------------- data rights */

export async function requestExport(): Promise<void> {
  await post('/account/export', null)
}

export async function deleteAccount(): Promise<void> {
  await call('/account', null, { method: 'DELETE' })
}

/* ------------------------------------------------------------------- parent */

/** `PATCH /api/parent` (WP-E). The cron reads these windows, so an edit has to reach it. */
export async function updateParent(patch: {
  windows?: WindowsConfig
  locale?: 'tr' | 'en'
}): Promise<Parent> {
  return (await call('/parent', ParentSchema, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })) as Parent
}
